import { getFullYear } from "./dates";
import { formatSourceDivision, formatTitleCase } from "./format";
import { lookupLocation } from "./locations";
import { SITE_BUSINESS_UNIT_OPTIONS } from "../constants";

export const normalizeListing = listing => {
  // end early if undefined
  if (!listing || listing.constructor.name !== "Object") {
    return null;
  }

  // handle null or zero value for category_id
  if (
    listing.hasOwnProperty("category_id") &&
    [null, 0, "0"].includes(listing.category_id)
  ) {
    listing.category_id = "";
  }

  // Check if this listing has photos
  if (!!listing.photos && typeof listing.photos === "string") {
    listing.photos = JSON.parse(listing.photos);
  }

  listing.photos = Array.isArray(listing.photos) ? listing.photos : [];

  // Process photos into a predictable shape
  const { photosLive, photosInternal } = listing.photos.reduce(
    (processed, photo) => {
      try {
        if (!photo || typeof photo === "string" || photo instanceof String) {
          throw new Error("Unexpected value");
        }

        if (!photo.hasOwnProperty("original")) {
          throw new Error("Invalid shape");
        }

        if (!photo.hasOwnProperty("availableIndex")) {
          photo.availableIndex = 0;
        } else if (isNaN(photo.availableIndex)) {
          const availableIndex = parseInt(photo.availableIndex);
          photo.availableIndex = !isNaN(availableIndex) ? availableIndex : 0;
        }

        if (!photo.hasOwnProperty("lastModified")) {
          photo.lastModified = "";
        } else if (photo.lastModified.hasOwnProperty("date")) {
          photo.lastModified = photo.lastModified.date;
        }

        if (!!photo.liveCropped) {
          if (!photo.hasOwnProperty("liveIndex")) {
            photo.liveIndex = 0;
          } else if (isNaN(photo.liveIndex)) {
            const liveIndex = parseInt(photo.liveIndex);
            photo.liveIndex = !isNaN(liveIndex) ? liveIndex : 0;
          }

          processed.photosLive.push(photo);
        } else if (!!photo.original) {
          processed.photosInternal.push(photo);
        }
      } catch (err) {}

      return processed;
    },
    {
      photosLive: [],
      photosInternal: [],
    }
  );

  listing.photos = photosLive.sort((a, b) => a.liveIndex - b.liveIndex);
  listing.photosInternal = photosInternal;

  // Normalize formatting
  if (!!listing.year) {
    listing.year = getFullYear(listing.year);
  }

  if (!!listing.year && listing.year === "9999") {
    listing.year = "";
  }

  if (!!listing.make) {
    listing.make = listing.make.toUpperCase();
  }

  if (!listing.meterType) {
    listing.meterType = "meter";
  }

  if (!listing.meterValue && listing.meterValue !== 0) {
    listing.meterValue = undefined;
  }

  if (!!listing.model) {
    listing.model = listing.model.toUpperCase();
  }

  if (!!listing.locationCity) {
    listing.locationCity =
      listing.locationCity.toLowerCase() === "n/a"
        ? ""
        : formatTitleCase(listing.locationCity.trim());
  }

  if (!!listing.locationState) {
    const loc = lookupLocation(listing.locationState);

    if (loc) {
      listing.locationState = loc.region.abbr;

      if (!listing.locationCountry) {
        listing.locationCountry = loc.country.name;
      }
    }
  }

  if (!listing.locationZip || listing.locationZip === "0") {
    listing.locationZip = "";
  }

  // Normalize isInternal flag
  if (listing.hasOwnProperty("isInternal")) {
    listing.isInternal = parseInt(listing.isInternal, 10);
  }

  // Get user-friendly business unit
  if (listing.hasOwnProperty("sourceDivision") && !!listing.sourceDivision) {
    const businessUnitSource = `${listing.sourceDivision}`.split(",")[0];
    const { id: businessUnit, label: businessUnitFormatted } =
      SITE_BUSINESS_UNIT_OPTIONS.find(businessUnit =>
        businessUnit.sources.includes(businessUnitSource)
      ) || {};
    listing.businessUnit = businessUnit;
    listing.businessUnitFormatted = businessUnitFormatted;
  }

  // Get user-friendly source division
  if (listing.hasOwnProperty("sourceDivision")) {
    listing.sourceDivision = formatSourceDivision(listing.sourceDivision);
  }

  // Listing title and SEO description
  let titleParts = [];

  if (!!listing.year) {
    titleParts.push(listing.year);
  }

  if (!!listing.make) {
    titleParts.push(listing.make);
  }

  if (!!listing.make && !!listing.model) {
    titleParts.push("–");
  }

  if (!!listing.model) {
    titleParts.push(listing.model);
  }

  if (titleParts.length > 0) {
    listing.title = titleParts.join(" ");
  }

  listing.seoDescription = `Photos, inspection report and features of this used ${listing.title}`;

  // Process attachments into a predictable shape
  try {
    if (!listing.hasOwnProperty("attachments")) {
      throw new Error("Listing attachments not defined");
    }

    if (
      !Array.isArray(listing.attachments) &&
      listing.attachments.constructor.name === "Object"
    ) {
      listing.attachments = Object.entries(listing.attachments).reduce(
        (aa, [key, value]) => {
          aa.push(value);
          return aa;
        },
        []
      );
    }

    if (!Array.isArray(listing.attachments) || !listing.attachments.length) {
      throw new Error("No listing attachments");
    }

    const { attachments, attachmentsInternal, attachmentsPublic } =
      listing.attachments.reduce(
        (processed, attachment) => {
          if (attachment.hasOwnProperty("isPublic")) {
            attachment.isPublic =
              !!attachment.isPublic &&
              attachment.isPublic !== "0" &&
              attachment.isPublic !== "false";
          } else {
            attachment.isPublic = false;
          }

          processed.attachments.push(attachment);

          if (attachment.isPublic) {
            processed.attachmentsPublic.push({ ...attachment });
          } else {
            processed.attachmentsInternal.push({ ...attachment });
          }

          return processed;
        },
        {
          attachments: [],
          attachmentsInternal: [],
          attachmentsPublic: [],
        }
      );

    listing.attachments = attachments;
    listing.attachmentsInternal = attachmentsInternal;
    listing.attachmentsPublic = attachmentsPublic;
  } catch (err) {
    listing.attachments = [];
    listing.attachmentsInternal = [];
    listing.attachmentsPublic = [];
  }

  listing.allowBuy =
    !!listing.allowBuy &&
    listing.allowBuy !== "false" &&
    listing.allowBuy !== "0";

  if (!listing.hasOwnProperty("includeWarranty")) {
    listing.includeWarranty = false;
  } else {
    listing.includeWarranty =
      !!listing.includeWarranty &&
      listing.includeWarranty !== "false" &&
      listing.includeWarranty !== "0";
  }

  return listing;
};

export const normalizeListingsData = listingsData => {
  listingsData =
    !!listingsData && Array.isArray(listingsData) ? listingsData : [];

  if (!!listingsData.length) {
    listingsData = listingsData
      .map(normalizeListing)
      .filter(listing => !!listing);
  }

  return listingsData;
};

export const displayMeter = ({ meterValue, sourceDivision }) =>
  (meterValue || meterValue === 0) &&
  !["attachments", "attachemnts"].includes(`${sourceDivision.toLowerCase()}`);

export const getEquipmentIdFromSlug = slug => {
  try {
    if (!slug) {
      return "";
    }

    slug = `${slug}`.toLowerCase().replace(/-near-.*/, "");

    const equipmentId = slug.match(/(us-)?[^-]+$/i, "$1");

    return !!equipmentId ? equipmentId[0] : "";
  } catch (err) {
    return "";
  }
};
